/**
 * Submit button should be isolated from other form elements based on UI design
 * It's might needed to be aligned left/center/right, but still in it's own row
 *
 * @example
 * <SubmitRow lobState={carLobState} />
 */
import * as React from "react";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { OffsetGridItem } from "components/shared/OffsetGridItem/OffsetGridItem";
import { LOBState } from "src/stores/wizard/state";
import { SubmitButton } from "src/components/shared/SubmitButton/SubmitButton";
import { getShoppingGlobalMicroMessage } from "components/shared/StorefrontWizard/SubmitRow/UISShoppingGlobal";
import { observer } from "mobx-react";
import { GlobalConfig } from "stores/wizard/config";

export interface SubmitRowProps {
  lobState: LOBState;
  rfrr?: string;
  rfrrid?: string;
  globalConfig?: GlobalConfig;
  disabled?: boolean;
  hasStickyWizard?: boolean;
  testId?: string;
  extraUISTracking?: any;
}

export const SubmitRow: React.FunctionComponent<SubmitRowProps> = observer(
  ({ lobState, rfrr, globalConfig, disabled = false, hasStickyWizard, testId, extraUISTracking }) => {
    const isHalfWizard = globalConfig?.halfWizard;
    const submitGridcolumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 8, large: 12 };
    const offsetColumn: UitkLayoutConditionalGridSpan = { medium: 3, large: isHalfWizard ? 3 : 5 };
    const submitColSpan: UitkLayoutConditionalGridSpan = {
      small: 1,
      medium: isHalfWizard ? 8 : 2,
      large: isHalfWizard ? 6 : 2,
    };

    const wizardRegionContext = React.useContext(StorefrontWizardRegionTypesContext);
    const { STOREFRONT_WIZARD_REGION_BEX, STOREFRONT_WIZARD_REGION_COMET } = StorefrontWizardRegionTypes;
    const isStorefrontBEX = STOREFRONT_WIZARD_REGION_BEX === wizardRegionContext;
    const isStorefrontCOMET = STOREFRONT_WIZARD_REGION_COMET === wizardRegionContext;
    const centerSubmitButton = isStorefrontBEX || isStorefrontCOMET;

    const uisShoppingGlobal = getShoppingGlobalMicroMessage(lobState);

    if (extraUISTracking) {
      uisShoppingGlobal.push(extraUISTracking);
    }

    if (hasStickyWizard) {
      return (
        <StickyWizardSubmitButton
          lobState={lobState}
          submitColSpan={submitColSpan}
          rfrr={rfrr}
          uisShoppingGlobal={uisShoppingGlobal}
          globalConfig={globalConfig}
          disabled={disabled}
          testId={testId}
        />
      );
    }

    return (
      <UitkSpacing padding={{ small: { blockstart: "three", blockend: "six" }, medium: { blockstart: "three" } }}>
        <UitkLayoutGrid columns={submitGridcolumns} space="three">
          {centerSubmitButton && <OffsetGridItem colSpan={offsetColumn} />}

          <SubmitButton
            lobState={lobState}
            colSpan={submitColSpan}
            rfrr={rfrr}
            uisPrimeMicroMessages={uisShoppingGlobal}
            globalConfig={globalConfig}
            disabled={disabled}
            testId={testId}
          />
        </UitkLayoutGrid>
      </UitkSpacing>
    );
  }
);

interface StickyWizardSubmitButtonProps
  extends Pick<SubmitRowProps, "lobState" | "rfrr" | "globalConfig" | "disabled" | "testId"> {
  submitColSpan: UitkLayoutConditionalGridSpan;
  uisShoppingGlobal: any;
}

const StickyWizardSubmitButton: React.FC<StickyWizardSubmitButtonProps> = ({
  lobState,
  rfrr,
  globalConfig,
  disabled,
  submitColSpan,
  uisShoppingGlobal,
  testId,
}) => (
  <SubmitButton
    lobState={lobState}
    colSpan={submitColSpan}
    rfrr={rfrr}
    uisPrimeMicroMessages={uisShoppingGlobal}
    globalConfig={globalConfig}
    disabled={disabled}
    testId={testId}
  />
);
